import { useQueryClient } from '@tanstack/react-query';
import { useVaultAddress } from 'src/hooks/useVaultAddress';
import { CommonVariables, WriteContractVariables } from 'src/libs/wagmi';
import { vaultAbi } from 'src/web3/vault-contract.abi';
import { Address } from 'viem';
import { Config, UseWriteContractParameters, useAccount, useWriteContract } from 'wagmi';
import { userKeys } from '../user/user-keys';

const commonVariables = {
  abi: vaultAbi,
  functionName: 'withdrawSchnorr',
} satisfies CommonVariables<typeof vaultAbi, 'withdrawSchnorr'>;

export interface WithdrawSchnorrVariables {
  combinedPublicAddress: string | Address;
  schnorr: {
    data: Address | string | `0x${string}`;
    signature: string | `0x${string}`;
    combinedPublicKey: string | `0x${string}`;
  };
  vaultAddress: Address;
}

export const buildWithdrawSchnorrVariables = ({
  combinedPublicAddress,
  schnorr,
  vaultAddress,
}: WithdrawSchnorrVariables): WriteContractVariables<typeof vaultAbi, 'withdrawSchnorr'> => {
  return {
    ...commonVariables,
    args: [
      combinedPublicAddress as `0x${string}`,
      {
        data: schnorr.data as `0x${string}`,
        signature: schnorr.signature as `0x${string}`,
        combinedPublicKey: combinedPublicAddress as `0x${string}`,
      },
    ],
    address: vaultAddress,
  };
};

export type UseDepositOptions<TContext> = UseWriteContractParameters<Config, TContext>;

export const useSchnorrWithdrawContract = <TContext>({
  mutation,
  ...options
}: UseDepositOptions<TContext> = {}) => {
  const queryClient = useQueryClient();
  const { address: userAddress } = useAccount();
  const vaultAddress = useVaultAddress();

  const withdrawSchnorrMutation = useWriteContract({
    mutation: {
      onSuccess: (_, { args }) => {
        return queryClient.invalidateQueries({ queryKey: userKeys.allBalances(userAddress!) });
      },
      onError: () => {},
      ...mutation,
    },

    ...options,
  });

  return withdrawSchnorrMutation;
};
